import { useRef, useState, useCallback, useEffect } from 'react';
import { Upload, X, File, Loader2, Image, FileText, FileArchive } from 'lucide-react';
import { attachmentsApi, type UploadedAttachment } from '../api/attachments';
import toast from 'react-hot-toast';

interface FileUploaderProps {
  attachments: UploadedAttachment[];
  onChange: (attachments: UploadedAttachment[]) => void;
  maxFiles?: number;
  context?: string;
  compact?: boolean;
  className?: string;
  /** When true, pasting an image uploads it (screenshots). Page-wide unless
   *  `pasteScopeRef` narrows it to one region. */
  enablePaste?: boolean;
  /** Limits paste-to-attach to pastes landing inside this element, so a
   *  reply box can accept screenshots without hijacking every paste on the
   *  page — which is why enablePaste is off by default. */
  pasteScopeRef?: React.RefObject<HTMLElement | null>;
  /** Hide the drop zone and show only the attached-file list. Lets a caller
   *  keep the uploader mounted (and paste live) while its panel is collapsed. */
  showDropZone?: boolean;
  /** Fired after files land via paste — e.g. to reveal a collapsed panel. */
  onFilesAdded?: () => void;
  /** Inline-image hooks: each pasted image reports upload start (with a
   *  stable id) so the compose box can drop a placeholder at the caret… */
  onPasteImageStart?: (id: string, file: File) => void;
  /** …and completion — att on success (swap in the real reference), null on
   *  failure (remove the placeholder). */
  onPasteImageDone?: (id: string, att: UploadedAttachment | null) => void;
}

function FileTypeIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-primary shrink-0" />;
  if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 shrink-0" style={{ color: 'var(--scpa-status-error)' }} />;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return <FileArchive className="w-4 h-4 shrink-0" style={{ color: 'var(--scpa-status-warning)' }} />;
  return <File className="w-4 h-4 text-muted shrink-0" />;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileUploader({
  attachments,
  onChange,
  maxFiles = 10,
  context = 'ticket',
  compact = false,
  className = '',
  enablePaste = false,
  pasteScopeRef,
  showDropZone = true,
  onFilesAdded,
  onPasteImageStart,
  onPasteImageDone,
}: FileUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);

  // Returns one entry per input file (null = failed/rejected), so the paste
  // handler can pair each upload with its inline placeholder.
  const uploadFiles = useCallback(async (files: FileList | File[]): Promise<(UploadedAttachment | null)[]> => {
    const fileArr = Array.from(files);
    if (attachments.length + fileArr.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return fileArr.map(() => null);
    }

    const uploadingNames = fileArr.map(f => f.name);
    setUploading(prev => [...prev, ...uploadingNames]);

    const results = await Promise.allSettled(
      fileArr.map(f => attachmentsApi.upload(f, context))
    );

    const perFile = results.map((r, i) => {
      if (r.status === 'fulfilled') return r.value;
      toast.error(`Failed to upload ${fileArr[i].name}: ${(r.reason as Error).message}`);
      return null;
    });

    const succeeded = perFile.filter((a): a is UploadedAttachment => a !== null);
    if (succeeded.length > 0) onChange([...attachments, ...succeeded]);
    setUploading(prev => prev.filter(n => !uploadingNames.includes(n)));
    return perFile;
  }, [attachments, maxFiles, context, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files);
  }, [uploadFiles]);

  // Paste-to-attach (screenshots). Grabs image files from the clipboard.
  useEffect(() => {
    if (!enablePaste) return;
    const onPaste = (e: ClipboardEvent) => {
      // Scoped mode: ignore pastes aimed outside the owning region, so
      // copying a screenshot into some other field doesn't attach it here.
      const scope = pasteScopeRef?.current;
      if (scope && !(e.target instanceof Node && scope.contains(e.target))) return;

      const files = Array.from(e.clipboardData?.items ?? [])
        .filter(it => it.kind === 'file' && it.type.startsWith('image/'))
        .map(it => it.getAsFile())
        .filter((f): f is File => !!f);
      if (files.length) {
        e.preventDefault();
        onFilesAdded?.();
        if (onPasteImageStart) {
          const ids = files.map(() => Math.random().toString(36).slice(2, 10));
          files.forEach((f, i) => onPasteImageStart(ids[i], f));
          void uploadFiles(files).then(perFile =>
            perFile.forEach((att, i) => onPasteImageDone?.(ids[i], att))
          );
        } else {
          void uploadFiles(files);
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [enablePaste, uploadFiles, pasteScopeRef, onFilesAdded, onPasteImageStart, onPasteImageDone]);

  const remove = async (att: UploadedAttachment) => {
    onChange(attachments.filter(a => a.path !== att.path));
    try { await attachmentsApi.delete(att.path); } catch { /* best-effort */ }
  };

  const isUploading = uploading.length > 0;

  // Collapsed with nothing to show: render nothing, but stay mounted so the
  // paste listener above keeps working.
  if (!showDropZone && attachments.length === 0 && !isUploading) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Drop zone */}
      {showDropZone && <div
        className={`border-2 border-dashed rounded-lg transition-colors cursor-pointer ${
          compact ? 'p-3' : 'p-5'
        } ${
          dragOver
            ? 'border-primary bg-primary-muted'
            : 'border-line hover:border-primary hover:bg-row-odd'
        }`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => e.target.files && uploadFiles(e.target.files)}
          accept="image/*,.pdf,.doc,.docx,.txt,.md,.log,.json,.zip,.csv,.xls,.xlsx,.ppt,.pptx"
        />
        {isUploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-muted">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            Uploading {uploading.length} file{uploading.length !== 1 ? 's' : ''}…
          </div>
        ) : compact ? (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Upload className="w-4 h-4 text-muted" />
            <span><span className="text-primary font-medium">Attach files</span> or drag & drop</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-center">
            <Upload className="w-6 h-6 text-muted" />
            <p className="text-sm text-muted">
              <span className="text-primary font-medium">Click to upload</span> or drag & drop
            </p>
            <p className="text-xs text-muted">Images, PDFs, docs, zip — up to 50 MB each · max {maxFiles} files</p>
          </div>
        )}
      </div>}

      {/* Uploading indicator while the drop zone is hidden (e.g. pasted a
          screenshot with the attach panel collapsed). */}
      {!showDropZone && isUploading && (
        <div className="flex items-center gap-2 text-xs text-muted">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
          Uploading {uploading.length} file{uploading.length !== 1 ? 's' : ''}…
        </div>
      )}

      {/* Attached files */}
      {attachments.length > 0 && (
        <ul className="space-y-1">
          {attachments.map((att) => (
            <li
              key={att.path}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-row-odd border border-line-light group"
            >
              <FileTypeIcon mimeType={att.mimeType} />
              <a
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-sm truncate text-muted hover:text-primary"
                onClick={e => e.stopPropagation()}
              >
                {att.filename}
              </a>
              <span className="text-xs text-muted shrink-0">{formatSize(att.size)}</span>
              <button
                type="button"
                onClick={() => remove(att)}
                className="shrink-0 text-muted hover:text-err transition-colors"
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
